import { useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { RagChip } from "@/components/RagChip";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  UploadCloud,
  CircleCheck,
  CircleX,
  TriangleAlert,
  RotateCcw,
  FileSpreadsheet,
  Loader2,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Upload", "Validate", "Publish"];

export default function Import() {
  const { session } = useSession();
  const qc = useQueryClient();
  const fileRef = useRef();
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);

  const isAdmin = session?.role === "pmo_admin";

  if (!isAdmin) {
    return (
      <div className="fluent-card p-8 max-w-xl mx-auto text-center fluent-enter" data-testid="import-forbidden">
        <ShieldAlert size={40} className="mx-auto text-[#D97706] mb-3" />
        <h1 className="text-xl font-semibold text-[#201F1E]">PMO Admin required</h1>
        <p className="text-sm text-[#605E5C] mt-2">
          Importing, publishing and resetting demo data are restricted to the <b>PMO Admin</b> persona.
          Scope is enforced server-side — switch persona from the top bar to continue.
        </p>
      </div>
    );
  }

  const onValidate = async (f) => {
    if (!f) return;
    setBusy(true);
    setStep(1);
    try {
      const form = new FormData();
      form.append("file", f);
      const res = await api.post("/import/validate", form, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Validation failed");
      setStep(0);
    } finally {
      setBusy(false);
    }
  };

  const onPublish = async () => {
    setBusy(true);
    try {
      const res = await api.post("/import/publish", { run_id: result.run_id, ack_warnings: ack });
      toast.success(`Published ${res.data.row_count} rows across ${res.data.months.length} month(s)`);
      setStep(2);
      qc.invalidateQueries();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publish failed");
    } finally {
      setBusy(false);
    }
  };

  const onReset = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/reset");
      const preserved = res.data?.preserved_months || [];
      toast.success(preserved.length
        ? `Seed restored. Preserved your imported month(s): ${preserved.join(", ")}`
        : "Demo data reset to synthetic baseline");
      qc.invalidateQueries();
      reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setStep(0);
    setFile(null);
    setResult(null);
    setAck(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="space-y-5 max-w-4xl fluent-enter" data-testid="import-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-[#201F1E]">Import Wizard</h1>
          <p className="text-sm text-[#605E5C] mt-1">Upload → Validate → Publish. Publishing creates an immutable new revision per month.</p>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" disabled={busy} data-testid="reset-demo-button" className="h-9 text-sm border-[#A80000] text-[#A80000] hover:bg-[#FDE7E9] fluent-focus">
              <RotateCcw size={14} className="mr-1.5" /> Reset demo data
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="reset-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Reset demo data?</AlertDialogTitle>
              <AlertDialogDescription>
                This restores the synthetic sample months. Any month you have <b>imported</b> will be <b>preserved</b> (not overwritten). Your imported uploads and their overrides stay intact.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="reset-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="reset-confirm" onClick={onReset} className="bg-[#A80000] hover:bg-[#8A0000]">
                Reset (keep my imports)
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`flex items-center gap-2 rounded px-3 py-1.5 text-xs font-semibold ${i === step ? "bg-[#0078D4] text-white" : i < step ? "bg-[#DFF6DD] text-[#107C41]" : "bg-[#F3F2F1] text-[#605E5C]"}`} data-testid={`step-${i}`}>
              <span className="h-4 w-4 rounded-full flex items-center justify-center text-[10px]" style={{ background: i <= step ? "rgba(255,255,255,0.3)" : "#D2D0CE" }}>{i + 1}</span>
              {s}
            </div>
            {i < STEPS.length - 1 && <div className="w-8 h-px bg-[#D2D0CE]" />}
          </div>
        ))}
      </div>

      {/* Step 0: upload */}
      {step === 0 && (
        <div className="fluent-card p-8">
          <div
            className="border-2 border-dashed border-[#C7E0F4] rounded-lg p-10 text-center bg-[#F9FCFF] cursor-pointer hover:border-[#0078D4] transition-colors"
            onClick={() => fileRef.current?.click()}
            data-testid="upload-dropzone"
          >
            <UploadCloud size={40} className="mx-auto text-[#0078D4] mb-3" />
            <p className="text-sm font-semibold text-[#201F1E]">Click to choose an .xlsx or .csv file</p>
            <p className="text-xs text-[#605E5C] mt-1">Monthly PMO report (primary) or weekly 26-field financial format (auto-detected)</p>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.csv"
              className="hidden"
              data-testid="import-file-input"
              onChange={(e) => { const f = e.target.files[0]; setFile(f); onValidate(f); }}
            />
          </div>
        </div>
      )}

      {/* Step 1: validate */}
      {step === 1 && (
        <div className="space-y-4">
          {busy || !result ? (
            <div className="fluent-card p-10 text-center"><Loader2 className="animate-spin inline text-[#0078D4]" size={26} /><p className="text-sm text-[#605E5C] mt-3">Parsing & validating…</p></div>
          ) : (
            <>
              <div className="fluent-card p-4 flex flex-wrap items-center gap-4" data-testid="validate-summary">
                <div className="flex items-center gap-2"><FileSpreadsheet size={18} className="text-[#0078D4]" /><span className="text-sm font-semibold">{file?.name}</span></div>
                <Badge label={`Format: ${result.format}`} tone="blue" />
                <Badge label={`${result.row_count} valid rows`} tone="green" />
                {result.error_count > 0 && <Badge label={`${result.error_count} errors`} tone="red" />}
                {result.warning_count > 0 && <Badge label={`${result.warning_count} warnings`} tone="amber" />}
                <span className="text-xs text-[#605E5C]">Months: {result.months.join(", ") || "—"}</span>
              </div>

              {result.errors.length > 0 && (
                <div className="fluent-card p-4" data-testid="validate-errors">
                  <div className="flex items-center gap-2 mb-2 text-[#A80000] font-semibold text-sm"><CircleX size={16} /> Errors — file rejected (atomic)</div>
                  <ul className="text-xs space-y-1 max-h-40 overflow-y-auto">
                    {result.errors.map((e, i) => <li key={i} className="text-[#A80000]">Row {e.row}: {e.message}</li>)}
                  </ul>
                </div>
              )}

              {result.warnings.length > 0 && (
                <div className="fluent-card p-4" data-testid="validate-warnings">
                  <div className="flex items-center gap-2 mb-2 text-[#8A6D00] font-semibold text-sm"><TriangleAlert size={16} /> Warnings — publishable with acknowledgement</div>
                  <ul className="text-xs space-y-1 max-h-40 overflow-y-auto">
                    {result.warnings.map((w, i) => <li key={i} className="text-[#8A6D00]">Row {w.row}: {w.message}</li>)}
                  </ul>
                </div>
              )}

              {/* Preview */}
              <div className="fluent-card overflow-hidden" data-testid="validate-preview">
                <div className="px-4 py-2.5 bg-[#F3F2F1] text-xs font-semibold uppercase tracking-wide text-[#605E5C]">Preview (first {result.preview.length})</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-[#605E5C] border-b border-[#EDEBE9]">
                      <th className="px-3 py-2">Project</th><th className="px-3 py-2">Month</th><th className="px-3 py-2">Platform</th><th className="px-3 py-2">%</th><th className="px-3 py-2">Health</th>
                    </tr></thead>
                    <tbody>
                      {result.preview.map((r, i) => (
                        <tr key={i} className="border-b border-[#F3F2F1]">
                          <td className="px-3 py-2 font-semibold">{r.project_name}</td>
                          <td className="px-3 py-2">{r.month_label}</td>
                          <td className="px-3 py-2">{r.platform}</td>
                          <td className="px-3 py-2">{r.pct_completion ?? "—"}%</td>
                          <td className="px-3 py-2"><RagChip rag={r.rag} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <Button variant="outline" onClick={reset} className="h-9 text-sm fluent-focus" data-testid="cancel-import">Cancel</Button>
                <div className="flex items-center gap-3">
                  {result.can_publish && result.warning_count > 0 && (
                    <label className="flex items-center gap-2 text-xs text-[#323130] cursor-pointer">
                      <Checkbox checked={ack} onCheckedChange={setAck} data-testid="ack-warnings" /> Acknowledge {result.warning_count} warning(s)
                    </label>
                  )}
                  <Button
                    onClick={onPublish}
                    disabled={!result.can_publish || busy || (result.warning_count > 0 && !ack)}
                    data-testid="publish-button"
                    className="h-9 text-sm bg-[#008272] hover:bg-[#00695c] text-white fluent-focus disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="animate-spin mr-1.5" size={15} /> : <CircleCheck size={15} className="mr-1.5" />}
                    Publish month(s)
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 2: done */}
      {step === 2 && (
        <div className="fluent-card p-10 text-center" data-testid="publish-success">
          <CircleCheck size={44} className="mx-auto text-[#107C41] mb-3" />
          <h2 className="text-lg font-semibold text-[#201F1E]">Published & live across all screens</h2>
          <p className="text-sm text-[#605E5C] mt-2">Prior revisions were retained (immutable). Re-uploading the same month creates a new revision.</p>
          <div className="flex items-center justify-center gap-3 mt-5">
            <Button onClick={reset} className="h-9 text-sm bg-[#0078D4] hover:bg-[#005A9E] text-white fluent-focus" data-testid="import-another">Import another file</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Badge({ label, tone }) {
  const tones = {
    blue: "bg-[#EFF6FC] text-[#004578] border-[#C7E0F4]",
    green: "bg-[#DFF6DD] text-[#107C41] border-[#107C41]",
    red: "bg-[#FDE7E9] text-[#A80000] border-[#A80000]",
    amber: "bg-[#FFF4CE] text-[#8A6D00] border-[#B3B000]",
  };
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${tones[tone]}`}>{label}</span>;
}
